import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  MagnifyingGlass, Plus, PencilSimple, Trash, X, Sparkle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

// Category tints — solid, high-contrast (dark text on tinted bg).
const CATEGORY_STYLE = {
  strength:     { chip: "bg-orange-100 text-orange-800 border border-orange-300" },
  power:        { chip: "bg-amber-100  text-amber-900  border border-amber-300"  },
  olympic:      { chip: "bg-red-100    text-red-800    border border-red-300"    },
  plyometric:   { chip: "bg-yellow-100 text-yellow-900 border border-yellow-300" },
  speed:        { chip: "bg-cyan-100   text-cyan-800   border border-cyan-300"   },
  conditioning: { chip: "bg-green-100  text-green-800  border border-green-300"  },
  mobility:     { chip: "bg-purple-100 text-purple-800 border border-purple-300" },
  warmup:       { chip: "bg-lime-100   text-lime-800   border border-lime-300"   },
  activation:   { chip: "bg-lime-100   text-lime-800   border border-lime-300"   },
  accessory:    { chip: "bg-blue-100   text-blue-800   border border-blue-300"   },
  core:         { chip: "bg-sky-100    text-sky-800    border border-sky-300"    },
};

const DIFFICULTY_STYLE = {
  beginner:     "bg-green-100 text-green-800 border border-green-300",
  intermediate: "bg-amber-100 text-amber-900 border border-amber-300",
  advanced:     "bg-red-100 text-red-800 border border-red-300",
};

const CATEGORY_ORDER = [
  "strength", "power", "olympic", "plyometric", "speed", "conditioning",
  "mobility", "warmup", "activation", "accessory", "core",
];

// Shared class strings (light workspace: bg #FAFAF7, text #0F172A)
const CARD = "bg-white border border-slate-200 hover:border-brand-accent transition";
const PANEL = "bg-white border border-slate-200";
const SUBTLE = "text-slate-500";
const BODY = "text-slate-800";
const CHIP_NEUTRAL = "bg-slate-100 text-slate-700 border border-slate-200";
const CHIP_INACTIVE = "bg-white text-slate-600 border border-slate-300 hover:border-slate-400";

export default function CoachExerciseLibrary() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [muscle, setMuscle] = useState("");
  const [source, setSource] = useState("");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorTarget, setEditorTarget] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (category) params.set("category", category);
    if (difficulty) params.set("difficulty", difficulty);
    if (muscle) params.set("muscle", muscle);
    if (source) params.set("source", source);
    params.set("limit", "300");
    try {
      const [{ data: list }, { data: meta }] = await Promise.all([
        api.get(`/exercise-library?${params.toString()}`),
        api.get(`/exercise-library/filters`),
      ]);
      setItems(list.items || []);
      setFilters(meta);
    } catch { toast.error("Failed to load exercises"); }
    finally { setBusy(false); }
  }, [q, category, difficulty, muscle, source]);

  useEffect(() => { load(); }, [load]);

  const deleteExercise = async (ex) => {
    if (!ex.is_custom) return;
    if (!window.confirm(`Delete "${ex.name}"?`)) return;
    try {
      await api.delete(`/exercise-library/${ex.exercise_id}`);
      toast.success("Deleted");
      if (selected?.exercise_id === ex.exercise_id) setSelected(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openCreate = () => { setEditorTarget(null); setEditorOpen(true); };
  const openEdit = (ex) => { setEditorTarget(ex); setEditorOpen(true); };

  const resetFilters = () => {
    setQ(""); setCategory(""); setDifficulty(""); setMuscle(""); setSource("");
  };

  const grouped = useMemo(() => {
    const g = {};
    (items || []).forEach((r) => {
      g[r.category] = g[r.category] || [];
      g[r.category].push(r);
    });
    return g;
  }, [items]);

  return (
    <div className="max-w-7xl mx-auto p-6 lg:p-10" data-testid="coach-exercise-library">
      <div className="mb-6">
        <div className="label-tiny text-brand-accent mb-2">// COACH TOOLS</div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl">Exercise Library</h1>
            <p className={`${SUBTLE} text-sm mt-2`}>
              Rich metadata catalog. Seeded exercises are read-only; your custom entries are editable.
            </p>
          </div>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-1.5 bg-brand-accent text-white px-4 py-2 text-xs label-tiny tracking-wider hover:bg-brand-accentHover transition"
            data-testid="ex-new-btn"
          >
            <Plus size={12} weight="bold" /> ADD EXERCISE
          </button>
        </div>
      </div>

      {filters && (
        <div className={`${PANEL} p-4 mb-6 space-y-4`} data-testid="ex-filters">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input
                placeholder="Search by name, muscle, tag, equipment..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9 bg-white text-slate-900 border-slate-300 placeholder:text-slate-400"
                data-testid="ex-search"
              />
            </div>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              data-testid="ex-source"
              className="bg-white border border-slate-300 text-sm text-slate-800 px-3 py-2"
            >
              <option value="">All Sources ({filters.total})</option>
              <option value="seed">Built-in ({filters.total - filters.custom})</option>
              <option value="custom">My Custom ({filters.custom})</option>
            </select>
            {(q || category || difficulty || muscle || source) && (
              <button
                onClick={resetFilters}
                className="text-[10px] label-tiny tracking-wider px-2 py-1.5 border border-slate-300 text-slate-700 hover:border-slate-500 transition"
                data-testid="ex-reset-filters"
              >
                <X size={10} className="inline mr-1" /> CLEAR
              </button>
            )}
          </div>

          {/* Category chips */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`label-tiny text-[10px] ${SUBTLE} mr-1`}>CATEGORY</span>
            <button
              onClick={() => setCategory("")}
              className={`text-[10px] label-tiny tracking-wider px-2 py-1 transition ${
                category === "" ? "bg-brand-accent text-white border border-brand-accent" : CHIP_INACTIVE
              }`}
              data-testid="ex-cat-all"
            >ALL</button>
            {CATEGORY_ORDER.map((c) => {
              const count = filters.categories[c] || 0;
              if (count === 0) return null;
              const s = CATEGORY_STYLE[c];
              return (
                <button
                  key={c}
                  onClick={() => setCategory(category === c ? "" : c)}
                  className={`text-[10px] label-tiny tracking-wider px-2 py-1 transition ${
                    category === c ? s.chip : CHIP_INACTIVE
                  }`}
                  data-testid={`ex-cat-${c}`}
                >{c.toUpperCase()} <span className="opacity-70 ml-1">{count}</span></button>
              );
            })}
          </div>

          {/* Difficulty + muscle */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`label-tiny text-[10px] ${SUBTLE} mr-1`}>DIFFICULTY</span>
            {["beginner", "intermediate", "advanced"].map((d) => (
              <button
                key={d}
                onClick={() => setDifficulty(difficulty === d ? "" : d)}
                className={`text-[10px] label-tiny tracking-wider px-2 py-1 transition ${
                  difficulty === d ? DIFFICULTY_STYLE[d] : CHIP_INACTIVE
                }`}
                data-testid={`ex-diff-${d}`}
              >{d.toUpperCase()} <span className="opacity-70 ml-1">{filters.difficulties[d] || 0}</span></button>
            ))}
            <span className={`label-tiny text-[10px] ${SUBTLE} ml-4 mr-1`}>MUSCLE</span>
            <select
              value={muscle}
              onChange={(e) => setMuscle(e.target.value)}
              className="bg-white border border-slate-300 text-xs text-slate-800 px-2 py-1"
              data-testid="ex-muscle"
            >
              <option value="">All Muscles</option>
              {(filters.muscles || []).map((m) => (
                <option key={m.name} value={m.name}>{m.name} ({m.count})</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {busy && <div className={`label-tiny ${SUBTLE}`} data-testid="ex-loading">Loading...</div>}
      {!busy && items.length === 0 && (
        <div className={`${PANEL} p-10 text-center ${SUBTLE} text-sm`} data-testid="ex-empty">
          No exercises match this filter. <button onClick={resetFilters} className="text-brand-accent hover:underline ml-1">Clear filters</button> or <button onClick={openCreate} className="text-brand-accent hover:underline">create one</button>.
        </div>
      )}

      {Object.entries(grouped).sort(([a],[b])=>CATEGORY_ORDER.indexOf(a)-CATEGORY_ORDER.indexOf(b)).map(([cat, rows]) => (
        <div key={cat} className="mb-8" data-testid={`ex-group-${cat}`}>
          <div className="flex items-center gap-2 mb-3">
            <span className={`label-tiny text-[10px] px-2 py-0.5 ${CATEGORY_STYLE[cat]?.chip || CHIP_NEUTRAL}`}>{cat.toUpperCase()}</span>
            <span className={`label-tiny text-[10px] ${SUBTLE} tabular-nums`}>{rows.length}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {rows.map((ex) => (
              <button
                key={ex.exercise_id}
                onClick={() => setSelected(ex)}
                className={`text-left ${CARD} p-3 group`}
                data-testid={`ex-card-${ex.exercise_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className={`font-heading text-sm truncate flex items-center gap-1.5 ${BODY}`}>
                      {ex.name}
                      {ex.is_custom && <Sparkle size={10} weight="fill" className="text-brand-accent shrink-0" />}
                    </div>
                    <div className={`text-[11px] ${SUBTLE} truncate mt-0.5`}>
                      {ex.primary_muscle || "—"}{(ex.secondary_muscles?.length ? ` · +${ex.secondary_muscles.length}` : "")}
                    </div>
                  </div>
                  <span className={`label-tiny text-[9px] px-1.5 py-0.5 shrink-0 ${DIFFICULTY_STYLE[ex.difficulty] || CHIP_NEUTRAL}`}>
                    {(ex.difficulty || "—").toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center gap-1 flex-wrap mt-2">
                  {(ex.equipment || []).slice(0, 3).map((e) => (
                    <span key={e} className="text-[9px] label-tiny px-1.5 py-0.5 bg-slate-100 text-slate-700">{e}</span>
                  ))}
                  {(ex.tags || []).slice(0, 2).map((t) => (
                    <span key={t} className="text-[9px] label-tiny px-1.5 py-0.5 border border-slate-300 text-slate-600">#{t}</span>
                  ))}
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}

      {selected && (
        <ExerciseDetailDialog
          exercise={selected}
          onClose={() => setSelected(null)}
          onEdit={() => { openEdit(selected); setSelected(null); }}
          onDelete={() => deleteExercise(selected)}
        />
      )}

      {editorOpen && (
        <ExerciseEditorDialog
          target={editorTarget}
          onClose={() => setEditorOpen(false)}
          onSaved={() => { setEditorOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function ExerciseDetailDialog({ exercise, onClose, onEdit, onDelete }) {
  const s = CATEGORY_STYLE[exercise.category];
  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="ex-detail-dialog"
    >
      <div
        className="bg-white border border-slate-200 shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className={`label-tiny text-[10px] px-2 py-0.5 ${s?.chip || CHIP_NEUTRAL}`}>{exercise.category.toUpperCase()}</span>
              <span className={`label-tiny text-[10px] px-2 py-0.5 ${DIFFICULTY_STYLE[exercise.difficulty] || CHIP_NEUTRAL}`}>
                {(exercise.difficulty || "—").toUpperCase()}
              </span>
              <span className="label-tiny text-[10px] px-2 py-0.5 bg-slate-100 text-slate-700 border border-slate-200">
                {exercise.exercise_type?.toUpperCase() || "—"}
              </span>
              {exercise.is_custom && (
                <span className="label-tiny text-[10px] px-2 py-0.5 bg-brand-accent text-white inline-flex items-center gap-1">
                  <Sparkle size={9} weight="fill" /> CUSTOM
                </span>
              )}
            </div>
            <h2 className="font-heading text-3xl">{exercise.name}</h2>
            <p className={`text-xs ${SUBTLE} mt-1`}>
              Primary: <span className="text-slate-800">{exercise.primary_muscle || "—"}</span>
              {exercise.secondary_muscles?.length > 0 && (
                <> · Secondary: <span className="text-slate-700">{exercise.secondary_muscles.join(", ")}</span></>
              )}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="ex-detail-close">
            <X size={16} />
          </button>
        </div>

        {exercise.video_url && (
          <div className="border border-slate-200 aspect-video bg-black">
            <iframe
              src={exercise.video_url}
              title={exercise.name}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}

        {exercise.instructions && (
          <div>
            <div className={`label-tiny text-[10px] ${SUBTLE} mb-1`}>// INSTRUCTIONS</div>
            <p className="text-sm text-slate-800 whitespace-pre-wrap">{exercise.instructions}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {exercise.coach_cues?.length > 0 && (
            <div className="bg-slate-50 border border-slate-200 p-3">
              <div className="label-tiny text-[10px] text-brand-accent mb-2">// COACH CUES</div>
              <ul className="space-y-1 text-xs text-slate-800">
                {exercise.coach_cues.map((c, i) => <li key={i}>· {c}</li>)}
              </ul>
            </div>
          )}
          {exercise.common_mistakes?.length > 0 && (
            <div className="bg-red-50 border border-red-200 p-3">
              <div className="label-tiny text-[10px] text-red-700 mb-2">// COMMON MISTAKES</div>
              <ul className="space-y-1 text-xs text-slate-800">
                {exercise.common_mistakes.map((c, i) => <li key={i}>· {c}</li>)}
              </ul>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap text-[11px]">
          {exercise.equipment?.length > 0 && (
            <>
              <span className={SUBTLE}>Equipment:</span>
              {exercise.equipment.map((e) => (
                <span key={e} className="px-1.5 py-0.5 bg-slate-100 text-slate-800">{e}</span>
              ))}
            </>
          )}
        </div>
        {exercise.tags?.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap">
            {exercise.tags.map((t) => (
              <span key={t} className="text-[10px] label-tiny px-2 py-0.5 border border-slate-300 text-slate-600">#{t}</span>
            ))}
          </div>
        )}

        {exercise.is_custom && (
          <div className="flex gap-2 justify-end pt-3 border-t border-slate-200">
            <button
              onClick={onEdit}
              className="text-xs label-tiny tracking-wider px-3 py-1.5 border border-slate-300 text-slate-700 hover:border-brand-accent hover:text-brand-accent transition inline-flex items-center gap-1"
              data-testid="ex-detail-edit"
            ><PencilSimple size={11} /> EDIT</button>
            <button
              onClick={onDelete}
              className="text-xs label-tiny tracking-wider px-3 py-1.5 border border-red-300 text-red-700 hover:bg-red-50 transition inline-flex items-center gap-1"
              data-testid="ex-detail-delete"
            ><Trash size={11} /> DELETE</button>
          </div>
        )}
      </div>
    </div>
  );
}

const EDIT_CATEGORIES = CATEGORY_ORDER;
const EDIT_DIFFICULTIES = ["beginner", "intermediate", "advanced"];

function ExerciseEditorDialog({ target, onClose, onSaved }) {
  const editing = !!target;
  const [form, setForm] = useState(() => ({
    name: target?.name || "",
    category: target?.category || "strength",
    primary_muscle: target?.primary_muscle || "",
    secondary_muscles: (target?.secondary_muscles || []).join(", "),
    equipment: (target?.equipment || []).join(", "),
    difficulty: target?.difficulty || "intermediate",
    exercise_type: target?.exercise_type || "compound",
    is_bilateral: target?.is_bilateral ?? true,
    instructions: target?.instructions || "",
    coach_cues: (target?.coach_cues || []).join("\n"),
    common_mistakes: (target?.common_mistakes || []).join("\n"),
    tempo: target?.tempo || "",
    video_url: target?.video_url || "",
    tags: (target?.tags || []).join(", "),
  }));
  const [busy, setBusy] = useState(false);

  const toList = (s, sep) => (s || "").split(sep).map((x) => x.trim()).filter(Boolean);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    const payload = {
      name: form.name.trim(),
      category: form.category,
      primary_muscle: form.primary_muscle.trim() || null,
      secondary_muscles: toList(form.secondary_muscles, ","),
      equipment: toList(form.equipment, ","),
      difficulty: form.difficulty,
      exercise_type: form.exercise_type,
      is_bilateral: !!form.is_bilateral,
      instructions: form.instructions,
      coach_cues: toList(form.coach_cues, "\n"),
      common_mistakes: toList(form.common_mistakes, "\n"),
      tempo: form.tempo.trim() || null,
      video_url: form.video_url.trim() || null,
      tags: toList(form.tags, ","),
    };
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/exercise-library/${target.exercise_id}`, payload);
        toast.success("Saved");
      } else {
        await api.post("/exercise-library", payload);
        toast.success("Exercise created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const inputCls = "bg-white text-slate-900 border-slate-300 placeholder:text-slate-400";
  const selectCls = "w-full bg-white border border-slate-300 text-sm text-slate-800 px-2 py-2";

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => !busy && onClose()}
      data-testid="ex-editor-dialog"
    >
      <div
        className="bg-white border border-slate-200 shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="label-tiny text-brand-accent text-[10px]">// {editing ? "EDIT" : "NEW"} EXERCISE</div>
            <h2 className="font-heading text-2xl mt-1">{editing ? target.name : "Add custom exercise"}</h2>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="ex-editor-close"><X size={16} /></button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <label className={`label-tiny text-[10px] ${SUBTLE} block mb-1`}>NAME *</label>
            <Input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ex-editor-name" />
          </div>
          <div>
            <label className={`label-tiny text-[10px] ${SUBTLE} block mb-1`}>CATEGORY *</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={selectCls} data-testid="ex-editor-category">
              {EDIT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={`label-tiny text-[10px] ${SUBTLE} block mb-1`}>DIFFICULTY</label>
            <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} className={selectCls} data-testid="ex-editor-difficulty">
              {EDIT_DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className={`label-tiny text-[10px] ${SUBTLE} block mb-1`}>PRIMARY MUSCLE</label>
            <Input className={inputCls} value={form.primary_muscle} onChange={(e) => setForm({ ...form, primary_muscle: e.target.value })} placeholder="e.g. quads" data-testid="ex-editor-primary-muscle" />
          </div>
          <div className="md:col-span-3">
            <label className={`label-tiny text-[10px] ${SUBTLE} block mb-1`}>SECONDARY MUSCLES</label>
            <Input className={inputCls} value={form.secondary_muscles} onChange={(e) => setForm({ ...form, secondary_muscles: e.target.value })} placeholder="comma-separated" data-testid="ex-editor-secondary-muscles" />
          </div>
          <div className="md:col-span-2">
            <label className={`label-tiny text-[10px] ${SUBTLE} block mb-1`}>EQUIPMENT</label>
            <Input className={inputCls} value={form.equipment} onChange={(e) => setForm({ ...form, equipment: e.target.value })} placeholder="barbell, rack" data-testid="ex-editor-equipment" />
          </div>
          <div>
            <label className={`label-tiny text-[10px] ${SUBTLE} block mb-1`}>TYPE</label>
            <select value={form.exercise_type} onChange={(e) => setForm({ ...form, exercise_type: e.target.value })} className={selectCls} data-testid="ex-editor-type">
              <option value="compound">compound</option>
              <option value="isolation">isolation</option>
              <option value="unilateral">unilateral</option>
            </select>
          </div>
          <div>
            <label className={`label-tiny text-[10px] ${SUBTLE} block mb-1`}>TEMPO</label>
            <Input className={inputCls} value={form.tempo} onChange={(e) => setForm({ ...form, tempo: e.target.value })} placeholder="3-1-1-0" data-testid="ex-editor-tempo" />
          </div>
        </div>

        <div>
          <label className={`label-tiny text-[10px] ${SUBTLE} block mb-1`}>INSTRUCTIONS</label>
          <Textarea rows={3} className={inputCls} value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} data-testid="ex-editor-instructions" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className={`label-tiny text-[10px] ${SUBTLE} block mb-1`}>COACH CUES (one per line)</label>
            <Textarea rows={3} className={inputCls} value={form.coach_cues} onChange={(e) => setForm({ ...form, coach_cues: e.target.value })} data-testid="ex-editor-cues" />
          </div>
          <div>
            <label className={`label-tiny text-[10px] ${SUBTLE} block mb-1`}>COMMON MISTAKES (one per line)</label>
            <Textarea rows={3} className={inputCls} value={form.common_mistakes} onChange={(e) => setForm({ ...form, common_mistakes: e.target.value })} data-testid="ex-editor-mistakes" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className={`label-tiny text-[10px] ${SUBTLE} block mb-1`}>VIDEO URL (YouTube embed)</label>
            <Input className={inputCls} value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://www.youtube.com/embed/..." data-testid="ex-editor-video" />
          </div>
          <div>
            <label className={`label-tiny text-[10px] ${SUBTLE} block mb-1`}>TAGS (comma-separated)</label>
            <Input className={inputCls} value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="hinge, compound" data-testid="ex-editor-tags" />
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-xs cursor-pointer text-slate-800">
          <input
            type="checkbox"
            checked={!!form.is_bilateral}
            onChange={(e) => setForm({ ...form, is_bilateral: e.target.checked })}
            className="accent-brand-accent"
            data-testid="ex-editor-bilateral"
          />
          <span>Bilateral (both sides simultaneously)</span>
        </label>

        <div className="flex gap-2 justify-end pt-3 border-t border-slate-200">
          <button
            onClick={onClose}
            disabled={busy}
            className="text-xs label-tiny tracking-wider px-4 py-2 border border-slate-300 text-slate-700 hover:border-slate-500 disabled:opacity-40"
            data-testid="ex-editor-cancel"
          >CANCEL</button>
          <button
            onClick={save}
            disabled={busy}
            className="text-xs label-tiny tracking-wider px-4 py-2 bg-brand-accent text-white hover:bg-brand-accentHover disabled:opacity-50"
            data-testid="ex-editor-save"
          >{busy ? "SAVING..." : editing ? "SAVE CHANGES" : "CREATE"}</button>
        </div>
      </div>
    </div>
  );
}
